import { Component, contentChild, ElementRef, forwardRef, HostBinding, inject, Input, input, TemplateRef } from "@angular/core";
import { FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { CustomInput } from "@kernel/form/classes/custom-input";
import { CheckboxInputComponent } from "@kernel/form/modules/inputs/simple-inputs/components/checkbox-input/checkbox-input.component";
import { NgTemplateOutlet } from "@angular/common";
import { compareDeep } from "@kernel/shared/helpers";
import { FormErrorMessageComponent } from "@kernel/form/modules/tools/form-error-message/components/form-error-message/form-error-message.component";

@Component({
  selector: "sxw-checkbox-list",
  imports: [CheckboxInputComponent, NgTemplateOutlet, FormsModule, FormErrorMessageComponent],
  templateUrl: "./checkbox-list.component.html",
  styleUrl: "./checkbox-list.component.scss",
  host: {
    class: "checkbox-list",
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxListComponent),
      multi: true,
    },
  ],
})
export class CheckboxListComponent<TItem extends object = any> extends CustomInput<TItem[] | TItem[keyof TItem][]> {
  readonly elementRef = inject(ElementRef);

  items = input.required<TItem[]>();
  dataKey = input.required<keyof TItem>();
  @Input() override value: TItem[] | TItem[keyof TItem][] = [];
  groupName = input(`${Math.random().toString(32).slice(2)}`);
  checkboxIcon = input("pi pi-check");
  labelKey = input<keyof TItem>();
  valueKey = input<keyof TItem>();
  inputId = input<string>(crypto.randomUUID());
  size = input<"small" | "large">("large");

  @HostBinding("class") @Input() theme = "";
  checkboxTheme = input("");
  checkboxStyles = input<Record<string, string>>();

  customTemplate = contentChild<
    TemplateRef<{
      $implicit: TItem;
      value?: TItem[keyof TItem];
      label?: string;
      checked?: boolean;
    }>
  >("checkboxItemTemplate", { descendants: false });

  override writeValue(values: TItem[] | TItem[keyof TItem][] | null | undefined): void {
    super.writeValue(this.sanitizeValue(values));
    // use patchValue instead of onChange(), because onChange change the status of the form from outside like dirty
    // Note: check first if the values are different to prevent infinite loop, because patchValue will trigger writeValue again
    // TODO: find a better way to handle this
    if (!compareDeep(this.value, values)) {
      setTimeout(() => this.control?.patchValue(this.value));
    }
  }

  private sanitizeValue(values: TItem[] | TItem[keyof TItem][] | null | undefined) {
    if (!values) return [];
    const valueKey = this.valueKey();
    const items = this.items();
    if (valueKey) {
      const itemValues = new Set(items.map(item => item[valueKey]));
      return values?.filter(v => itemValues.has(v as TItem[keyof TItem])) as TItem[keyof TItem][];
    } else {
      return values?.filter(v => items.some(item => item[this.dataKey()] === (v as TItem)[this.dataKey()] || compareDeep(v, item))) as TItem[];
    }
  }

  checkboxChangeHandler(_: TItem | TItem[keyof TItem]) {
    this.onTouched();
    this.onChange(this.value);
  }
}
